import { useCallback, useEffect, useMemo, useState } from 'react'
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet'
import { geocodeAPI } from '../services/api'
import 'leaflet/dist/leaflet.css'
import './StationLocationMap.css'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 }
const DEFAULT_ZOOM = 5
const PIN_ZOOM = 16

function isFiniteLatLng(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
}

function parseLatLng(text) {
  const raw = String(text || '')
    .trim()
    .replace(/°/g, '')
    .replace(/\s+/g, ' ')
  if (!raw) return null
  const comma = raw.match(/^(-?\d+(?:\.\d+)?)\s*[,;]\s*(-?\d+(?:\.\d+)?)$/)
  if (comma) {
    const lat = Number(comma[1])
    const lng = Number(comma[2])
    if (isFiniteLatLng(lat, lng)) return { lat, lng }
  }
  const space = raw.match(/^(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)$/)
  if (space) {
    const lat = Number(space[1])
    const lng = Number(space[2])
    if (isFiniteLatLng(lat, lng)) return { lat, lng }
  }
  return null
}

function pinFromValue(value) {
  if (value?.latitude == null || value?.longitude == null || value.latitude === '' || value.longitude === '') {
    return null
  }
  const lat = Number(value.latitude)
  const lng = Number(value.longitude)
  if (!isFiniteLatLng(lat, lng)) return null
  return { lat, lng, name: String(value?.name || '').trim() }
}

function FlyToTarget({ target }) {
  const map = useMap()
  useEffect(() => {
    if (!target || !Number.isFinite(target.lat) || !Number.isFinite(target.lng)) return
    const z = Number.isFinite(target.zoom) ? target.zoom : PIN_ZOOM
    map.flyTo([target.lat, target.lng], z, { duration: 0.55 })
  }, [map, target])
  return null
}

function InvalidateSize() {
  const map = useMap()
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 80)
    return () => clearTimeout(t)
  }, [map])
  return null
}

function MapClickLayer({ onClick }) {
  useMapEvents({
    click(e) {
      onClick?.(e.latlng.lat, e.latlng.lng)
    },
  })
  return null
}

export default function StationLocationMap({ value, onChange }) {
  const pin = pinFromValue(value)
  const [searchInput, setSearchInput] = useState('')
  const [latInput, setLatInput] = useState(pin ? String(pin.lat) : '')
  const [lngInput, setLngInput] = useState(pin ? String(pin.lng) : '')
  const [nameInput, setNameInput] = useState(pin?.name || '')
  const [flyTo, setFlyTo] = useState(null)
  const [busy, setBusy] = useState(false)
  const [geoBusy, setGeoBusy] = useState(false)
  const [error, setError] = useState('')
  const [hint, setHint] = useState('')

  useEffect(() => {
    const next = pinFromValue(value)
    setLatInput(next ? String(next.lat) : '')
    setLngInput(next ? String(next.lng) : '')
    setNameInput(next?.name || '')
  }, [value?.latitude, value?.longitude, value?.name])

  const center = useMemo(
    () => (pin ? [pin.lat, pin.lng] : [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng]),
    [pin]
  )
  const zoom = pin ? PIN_ZOOM : DEFAULT_ZOOM

  const emitPin = useCallback((lat, lng, name, zoomLevel = PIN_ZOOM) => {
    const nextName = name != null ? String(name).trim() : String(nameInput || '').trim()
    setLatInput(String(lat))
    setLngInput(String(lng))
    if (name != null) setNameInput(nextName)
    setFlyTo({ lat, lng, zoom: zoomLevel })
    onChange?.({
      ...value,
      latitude: lat,
      longitude: lng,
      name: nextName,
      facilities: Array.isArray(value?.facilities) ? value.facilities : []
    })
  }, [nameInput, onChange, value])

  const fillNameIfEmpty = useCallback(async (lat, lng) => {
    if (String(nameInput || '').trim()) return
    try {
      const response = await geocodeAPI.reverse(lat, lng)
      const label = String(response.data?.label || '').trim()
      if (label) {
        setNameInput(label)
        onChange?.({
          ...value,
          latitude: lat,
          longitude: lng,
          name: label,
          facilities: Array.isArray(value?.facilities) ? value.facilities : []
        })
      }
    } catch {
      // Keep the pin even if reverse geocode is unavailable.
    }
  }, [nameInput, onChange, value])

  const onSearch = async () => {
    const q = searchInput.trim()
    if (!q) {
      setError('Enter a place, postal code, or lat, lng.')
      return
    }
    setBusy(true)
    setError('')
    setHint('')
    try {
      const parsed = parseLatLng(q)
      if (parsed) {
        emitPin(parsed.lat, parsed.lng, nameInput)
        setHint('Dropped pin at coordinates.')
        return
      }
      const response = await geocodeAPI.search(q)
      const lat = Number(response.data?.lat)
      const lng = Number(response.data?.lng)
      if (!isFiniteLatLng(lat, lng)) {
        throw new Error('Invalid search result')
      }
      const label = String(response.data?.label || q).trim()
      emitPin(lat, lng, label)
      setHint(`Found: ${label}`)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Location search failed')
    } finally {
      setBusy(false)
    }
  }

  const onApplyLatLng = () => {
    const parsed = parseLatLng(`${latInput}, ${lngInput}`)
    if (!parsed) {
      setError('Enter valid latitude and longitude.')
      return
    }
    setError('')
    emitPin(parsed.lat, parsed.lng, nameInput)
    setHint('Dropped pin from coordinates.')
    fillNameIfEmpty(parsed.lat, parsed.lng)
  }

  const onCurrentLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError('This browser does not support geolocation.')
      return
    }
    setGeoBusy(true)
    setError('')
    setHint('')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude
        const lng = pos.coords.longitude
        emitPin(lat, lng, nameInput || 'Current location')
        setHint('Dropped pin at your current location.')
        setGeoBusy(false)
        fillNameIfEmpty(lat, lng)
      },
      (err) => {
        const code = err?.code
        setError(
          code === 1
            ? 'Location permission denied. Allow location access in the browser, then try again.'
            : code === 2
              ? 'Location unavailable.'
              : code === 3
                ? 'Location request timed out.'
                : 'Could not read your current location.'
        )
        setGeoBusy(false)
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    )
  }

  const onMapClick = (lat, lng) => {
    setError('')
    emitPin(lat, lng, nameInput)
    setHint('Dropped pin on the map.')
    fillNameIfEmpty(lat, lng)
  }

  const onMarkerDrag = (event) => {
    const { lat, lng } = event.target.getLatLng()
    emitPin(lat, lng, nameInput)
    fillNameIfEmpty(lat, lng)
  }

  const onClear = () => {
    setLatInput('')
    setLngInput('')
    setNameInput('')
    setSearchInput('')
    setError('')
    setHint('Location cleared.')
    onChange?.({
      ...value,
      latitude: null,
      longitude: null,
      name: '',
      facilities: Array.isArray(value?.facilities) ? value.facilities : []
    })
  }

  const onNameBlur = () => {
    if (!pin) return
    onChange?.({
      ...value,
      latitude: pin.lat,
      longitude: pin.lng,
      name: String(nameInput || '').trim(),
      facilities: Array.isArray(value?.facilities) ? value.facilities : []
    })
  }

  return (
    <div className="station-location-map">
      <div className="station-location-map__search">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search place, postal code, or lat, lng"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onSearch()
            }
          }}
        />
        <button type="button" className="btn btn--sm btn--primary" onClick={onSearch} disabled={busy || geoBusy}>
          {busy ? 'Searching…' : 'Search'}
        </button>
        <button type="button" className="btn btn--sm btn--secondary" onClick={onCurrentLocation} disabled={busy || geoBusy}>
          {geoBusy ? 'Locating…' : 'Current location'}
        </button>
        {pin && (
          <button type="button" className="btn btn--sm btn--secondary" onClick={onClear} disabled={busy || geoBusy}>
            Clear
          </button>
        )}
      </div>

      <div className="form-grid">
        <div className="form-field">
          <label>Latitude</label>
          <input
            type="text"
            inputMode="decimal"
            value={latInput}
            onChange={(e) => setLatInput(e.target.value)}
            placeholder="e.g. 13.0827"
          />
        </div>
        <div className="form-field">
          <label>Longitude</label>
          <input
            type="text"
            inputMode="decimal"
            value={lngInput}
            onChange={(e) => setLngInput(e.target.value)}
            placeholder="e.g. 80.2707"
          />
        </div>
        <div className="form-field form-field--wide">
          <label>Location name</label>
          <input
            type="text"
            value={nameInput}
            onChange={(e) => {
              const name = e.target.value
              setNameInput(name)
              if (pin) {
                onChange?.({
                  ...value,
                  latitude: pin.lat,
                  longitude: pin.lng,
                  name,
                  facilities: Array.isArray(value?.facilities) ? value.facilities : []
                })
              }
            }}
            onBlur={onNameBlur}
            placeholder="Optional label for this pin"
          />
        </div>
      </div>

      <div className="station-location-map__coords-actions">
        <button type="button" className="btn btn--sm btn--secondary" onClick={onApplyLatLng}>
          Drop pin from coordinates
        </button>
        <small>Click the map to place or move the station pin.</small>
      </div>

      <div className="station-location-map__canvas">
        <MapContainer
          center={center}
          zoom={zoom}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <InvalidateSize />
          <FlyToTarget target={flyTo} />
          <MapClickLayer onClick={onMapClick} />
          {pin && (
            <Marker
              position={[pin.lat, pin.lng]}
              draggable
              eventHandlers={{ dragend: onMarkerDrag }}
            />
          )}
        </MapContainer>
      </div>

      {error && <div className="station-location-map__error">{error}</div>}
      {!error && hint && <div className="station-location-map__hint">{hint}</div>}
    </div>
  )
}
